import './styles.scss';
import '/node_modules/react-grid-layout/css/styles.css';
import '/node_modules/react-resizable/css/styles.css';

import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { BackgroundMessage } from '../../components/background-message';
import { PortfolioPerformanceContext } from '../../providers/portfolio-performance';
import { GlobalContext } from '../../providers/global-provider';

import PortfolioPerformanceContent from './portfolio-performance-content';
import { PortfolioPerformanceSidebar } from './portfolio-performance-sidebar';
import { WebSocketContext } from '../../providers/websocket-context/websocket-provider';
import { useNavigate } from 'react-router-dom';
import { useAccountGroups } from './hooks/use-account-groups';
import { GlobalTabsKeysEnum } from '../../providers/models/custom-app-links';
import { useReferenceStrategyTokens } from '../../providers/portfolio-performance/reference-strategies/use-reference-strategy-tokens';
import { loadSelectedAccountRefs, saveSelectedAccountRefs } from './selection-storage';

// Reference strategies now cover the full perps universe (~80+ tokens —
// see use-reference-strategy-tokens.ts), not just these 4, but defaulting
// ALL of them into selection would mean fetching candle history for
// tokens no client asked to look at. Only the majors are pre-selected;
// everything else is present in the sidebar (its own "reference" group)
// but opt-in.
const DEFAULT_REFERENCE_MAJORS = new Set(['BTC', 'ETH', 'SOL', 'XRP']);

const PorfolioPerfomance = () => {
  const { isConnected } = useContext(WebSocketContext);
  const { currentTab } = useContext(GlobalContext);
  const {
    credentials,
    loadingCredentials,
    errorCredentials,
    selectedAccounts,
    setSelectedAccounts,
  } = useContext(PortfolioPerformanceContext);
  const [isPortfolioPerformanceSidebarOpen, setIsPortfolioPerformanceSidebarOpen] = useState(true);
  const hasInitializedDefaultSelectionRef = useRef(false);
  const navigate = useNavigate();

  const contentClassname = `portfolio-sidebar-${isPortfolioPerformanceSidebarOpen ? 'open' : 'close'}`;

  const { tokens: referenceTokens, isLoading: loadingReferenceTokens } =
    useReferenceStrategyTokens();
  const referenceAccounts = useMemo(() => referenceTokens.map((t) => t.account), [referenceTokens]);
  const accountGroups = useAccountGroups(credentials, referenceAccounts);

  // Restores the sidebar selection from the last visit (localStorage), or
  // falls back to defaulting every connected real account plus only the
  // reference-strategy majors (not the full ~80+-token universe) —
  // previously only the first account was auto-selected/nothing was
  // remembered, leaving accounts hidden until manually ticked on again
  // every reload. Runs even when the credentials fetch itself errored
  // (errorCredentials) — that's a real accounts RPC failing server-side,
  // unrelated to the client-side reference strategies, which should still
  // be usable regardless.
  useEffect(() => {
    if (currentTab !== GlobalTabsKeysEnum.portfolioPerformance) return;
    if (hasInitializedDefaultSelectionRef.current) return;
    if (!isConnected || loadingCredentials || loadingReferenceTokens) return;

    const pool = [...credentials, ...referenceAccounts];
    if (pool.length === 0) return;

    if (selectedAccounts.length > 0) {
      hasInitializedDefaultSelectionRef.current = true;
      return;
    }

    // null means nothing has ever been saved (first-ever visit) — an empty
    // array is a real remembered choice (the user previously deselected
    // everything) and is respected as-is, not treated as "apply defaults".
    const savedRefs = loadSelectedAccountRefs();
    if (savedRefs !== null) {
      const restored = pool.filter((account) =>
        savedRefs.some((ref) => ref.name === account.name && ref.exchange === account.exchange),
      );
      // If every previously-selected account has since vanished (renamed,
      // removed, credentials revoked), fall through to defaults instead of
      // leaving the page looking empty.
      if (restored.length > 0) {
        setSelectedAccounts(restored);
        hasInitializedDefaultSelectionRef.current = true;
        return;
      }
    }

    const defaultAccounts = [
      ...credentials,
      ...referenceTokens
        .filter((t) => DEFAULT_REFERENCE_MAJORS.has(t.displaySymbol))
        .map((t) => t.account),
    ];
    if (defaultAccounts.length === 0) return;

    setSelectedAccounts(defaultAccounts);
    hasInitializedDefaultSelectionRef.current = true;
  }, [
    credentials,
    referenceAccounts,
    referenceTokens,
    currentTab,
    isConnected,
    loadingCredentials,
    loadingReferenceTokens,
    selectedAccounts,
    setSelectedAccounts,
  ]);

  // Persists the selection on every change so it survives the next reload.
  // Skips the very first run unconditionally (not gated on
  // hasInitializedDefaultSelectionRef) — that ref flips to true
  // synchronously inside the hydration effect above, in the same commit as
  // its own setSelectedAccounts(restored) call, so a guard checking the ref
  // here would still see selectedAccounts' STALE pre-hydration value (still
  // [] from this same render) and immediately overwrite the just-restored
  // selection in storage before the restored value ever got a chance to
  // reach a committed render.
  const skipNextPersistRef = useRef(true);
  useEffect(() => {
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return;
    }
    saveSelectedAccountRefs(selectedAccounts);
  }, [selectedAccounts]);

  // Real accounts RPC failing is a backend-side issue, unrelated to the
  // client-side reference strategies — caught here (logged, not blocking)
  // rather than in the render path below.
  useEffect(() => {
    if (errorCredentials) {
      console.warn('Portfolio Performance: failed to load connected exchange credentials.');
    }
  }, [errorCredentials]);

  if (loadingCredentials || loadingReferenceTokens || !isConnected) {
    return (
      <div className="portfolio-performance-background-message">
        <BackgroundMessage classname="background-message" isLoading />
      </div>
    );
  }

  const redirectToSettings = () => {
    navigate('/settings');
  };

  // A failed credentials fetch (errorCredentials) means real exchange
  // accounts couldn't be loaded, but accountGroups always includes the
  // client-side reference strategies regardless (once loadingReferenceTokens
  // clears, guarded above) — so don't block the whole page on it, only on
  // there being genuinely nothing to show.
  if (accountGroups.length === 0) {
    return (
      <div className="portfolio-performance-background-message">
        <BackgroundMessage
          classname="background-message"
          type="error"
          message={{
            title: 'There are no connected exchanges',
          }}
          button={{
            title: 'Connect to an exchange',
            onClick: redirectToSettings,
          }}
        />
      </div>
    );
  }

  return (
    <div className="portfolio-performance-container">
      <PortfolioPerformanceSidebar
        isSidebarOpen={isPortfolioPerformanceSidebarOpen}
        setIsSidebarOpen={setIsPortfolioPerformanceSidebarOpen}
        accountGroups={accountGroups}
      />

      <PortfolioPerformanceContent className={contentClassname} referenceTokens={referenceTokens} />
    </div>
  );
};

export default PorfolioPerfomance;
